import NodeCache from 'node-cache';
import { useCallback, useEffect, useRef } from 'react';
import { ApplicationConfig } from '../../config/config';
import { logger } from '../../util/logger';
import { useJwtProvider } from '../auth.hooks';
import {
  File as FileGql,
  FileUpload,
  SyncFileToMachineActionInput,
  useCreateFileUploadUrlMutation,
  useGetMachineFileLinkLazyQuery,
  useSyncFilesToMachineMutation,
} from '../graphql/generated';
import { fetchShared } from './network.hooks';

const fileLinkCache = new NodeCache({
  stdTTL: 300,
  checkperiod: 60,
});

const sendToS3 = async (
  file: File,
  presignedPost: FileUpload
): Promise<Response> => {
  const { url, fields } = presignedPost;
  const formData = new FormData();

  Object.keys(fields).forEach((key) => {
    formData.append(key, fields[key]);
  });

  const fileBlob: unknown = file;
  formData.append('file', fileBlob as Blob);

  const response = await fetch(url, {
    method: 'POST',
    body: formData,
  });

  return response;
};

type UploadFileFunction = (
  file: File,
  basePath?: string,
  key?: string
) => Promise<string | undefined>;

const useUploadFileLazy = (): [UploadFileFunction] => {
  const evictFile = useEvictFileCacheLazy();
  const [createFileUpload] = useCreateFileUploadUrlMutation();

  const uploadFile = useCallback(
    async (
      file: File,
      basePath?: string,
      key?: string
    ): Promise<string | undefined> => {
      const fileUpload = await createFileUpload({
        variables: {
          input: {
            basePath,
            contentType: file.type,
            key,
          },
        },
      });

      if (fileUpload?.data) {
        const { modifiedKey, ...presignedPost } =
          fileUpload.data.createFileUploadUrl;
        try {
          if (!modifiedKey) {
            return;
          }

          const response = await sendToS3(file, presignedPost);

          if (!response.ok) {
            throw new Error(response.statusText);
          }

          evictFile(modifiedKey);

          return modifiedKey;
        } catch (error) {
          logger.error('failed to upload to s3', {
            error,
            file: {
              name: file.name,
              size: file.size,
              type: file.type,
            },
            key: presignedPost.fields.key,
            url: presignedPost.url,
          });
        }
      }
    },
    [evictFile, createFileUpload]
  );

  return [uploadFile];
};

type GetFileLinkFunction = (url: string) => Promise<string | undefined>;

const getCacheKeyForFileUrl = (url: string) => {
  return url.split('#')[0];
};

const useEvictFileCacheLazy = (): ((
  url: string | undefined | null
) => void) => {
  const callback = useCallback((url: string | undefined | null) => {
    if (url) {
      fileLinkCache.del(getCacheKeyForFileUrl(url));
    }
  }, []);

  return callback;
};

const useGetFileLinkLazy = (): [GetFileLinkFunction] => {
  const jwtProvider = useJwtProvider({
    uri: ApplicationConfig.graphql.uri,
  });

  const getFileLink = useCallback(
    async (url: string): Promise<string | undefined> => {
      const cacheKey = getCacheKeyForFileUrl(url);
      const cachedFileLink = fileLinkCache.get<string>(cacheKey);

      if (cachedFileLink) {
        return cachedFileLink;
      }

      const jwt = await jwtProvider();

      if (!jwt) {
        return;
      }

      const location = `${ApplicationConfig.api.domain}/${jwt.tenantId}/file/?`;

      const fullUrl =
        location +
        new URLSearchParams({
          path: url,
          noRedirect: 'true',
        });

      let resolvedUrl: string | undefined;

      const body = await fetchShared(fullUrl, {
        headers: {
          Authorization: `Bearer ${jwt.token}`,
        },
      });

      if (body) {
        const payload = JSON.parse(body);
        fileLinkCache.set(cacheKey, payload.url, payload.expires || 300);
        resolvedUrl = payload.url;
      }

      return resolvedUrl;
    },
    [jwtProvider]
  );

  return [getFileLink];
};

const useGetMachineFileLink = (): ((
  item: FileGql,
  id?: string,
  expiresInSeconds?: number | null
) => Promise<string>) => {
  const resolveRef = useRef<(value: string | PromiseLike<string>) => void>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rejectRef = useRef<(reason?: any) => void>();

  const [getFileLink, { data, error }] = useGetMachineFileLinkLazyQuery({
    fetchPolicy: 'network-only',
  });
  const url = data?.getMachineFileLink.url;

  useEffect(() => {
    if (url && resolveRef.current) {
      resolveRef.current(url);
    }
  }, [url]);

  useEffect(() => {
    if (error?.message && rejectRef.current) {
      rejectRef.current(error);
    }
  }, [error]);

  const getLink = async (
    item: FileGql,
    id?: string,
    expiresInSeconds: number | null = null
  ) => {
    const { path, version } = item;
    if (id) {
      await getFileLink({
        variables: { input: { id, path, version, expiresInSeconds } },
      });
    }
  };

  return async (
    item: FileGql,
    id?: string,
    expiresInSeconds: number | null = null
  ): Promise<string> => {
    return new Promise((resolve, reject) => {
      resolveRef.current = resolve;
      rejectRef.current = reject;
      getLink(item, id, expiresInSeconds);
    });
  };
};

const useRestoreFiles = (): ((
  id: string,
  files: FileGql[]
) => Promise<boolean | undefined>) => {
  const [callRestoreFiles] = useSyncFilesToMachineMutation();

  const restoreFiles = async (
    id: string,
    files: FileGql[]
  ): Promise<boolean | undefined> => {
    const actions: SyncFileToMachineActionInput[] = files.map((file) => ({
      action: 'restore',
      path: file.path,
      version: file.version,
    }));

    const { data } = await callRestoreFiles({
      variables: { input: { id, actions } },
    });
    return data?.syncFilesToMachine;
  };
  return restoreFiles;
};

export {
  useEvictFileCacheLazy,
  useGetFileLinkLazy,
  useGetMachineFileLink,
  useRestoreFiles,
  useUploadFileLazy,
};
